import { useEffect, useRef, useState, type ReactNode } from 'react'

/**
 * Coque « feuille basse » (bottom sheet) GÉNÉRIQUE pour mobile (`<lg`). Le contenu
 * détail est injecté via `children` (c'est l'appelant qui passe `<SidePanel
 * variant="sheet" …>`), donc pas d'import circulaire avec `pages/Map`. Fermeture
 * par tap sur le fond, Échap, ou glissé vers le bas. Ne rend rien quand fermée.
 */
export function IslandDetailSheet({
  open,
  onClose,
  header,
  children,
}: {
  open: boolean
  onClose: () => void
  /** Contenu optionnel épinglé sous la poignée, dans le bloc sticky opaque
   *  (ex. le bandeau ressources de la carte) — reste visible pendant le scroll. */
  header?: ReactNode
  children: ReactNode
}) {
  const [dragY, setDragY] = useState(0)
  const startY = useRef<number | null>(null)
  const dragYRef = useRef(0)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open) setDragY(0)
  }, [open])

  if (!open) return null

  const onTouchStart = (e: React.TouchEvent) => {
    startY.current = e.touches[0].clientY
  }
  const onTouchMove = (e: React.TouchEvent) => {
    if (startY.current === null) return
    const dy = Math.max(0, e.touches[0].clientY - startY.current)
    dragYRef.current = dy
    setDragY(dy)
  }
  const onTouchEnd = () => {
    if (dragYRef.current > 80) onClose()
    setDragY(0)
    dragYRef.current = 0
    startY.current = null
  }

  return (
    <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
      <div
        data-testid="sheet-backdrop"
        className="absolute inset-0 bg-ocean-900/60 backdrop-blur-[1px]"
        onClick={onClose}
      />
      <div
        className="absolute inset-x-0 bottom-0 max-h-[82vh] overflow-y-auto rounded-t-2xl border-t border-sand-500/25 bg-ocean-900/95 shadow-2xl pb-[max(1rem,env(safe-area-inset-bottom))]"
        style={{ transform: dragY ? `translateY(${dragY}px)` : undefined, transition: dragY ? 'none' : 'transform 0.2s ease-out' }}
      >
        {/* Poignée + header optionnel : UN SEUL bloc sticky (deux siblings sticky
            top-0 se chevaucheraient), avec fond ocean opaque + léger blur pour
            rester lisible au-dessus du contenu qui défile dessous. Le blur reste
            sans danger ici : pas de descendant `fixed` (gotcha bloc conteneur). */}
        <div className="sticky top-0 z-10 bg-ocean-900/95 backdrop-blur-sm">
          <div
            className="grid place-items-center pt-2 pb-1 cursor-grab touch-none"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <span className="block h-1.5 w-10 rounded-full bg-sand-500/40" aria-hidden="true" />
          </div>
          {header}
        </div>
        <div className="px-1 pb-2">{children}</div>
      </div>
    </div>
  )
}
